/**
 * Usage Metrics Page
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { FunctionMetric, CostSummary, SlowFunction, MostCalledFunction, MetricsTrend } from '../api/types';
import { Card, CardContent, CardHeader, CardTitle, StatCard } from '../components/Card';
import { Button } from '../components/Button';
import { Input, Select } from '../components/Input';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  EmptyState,
  Badge,
} from '../components/Table';
import { Activity, Clock, DollarSign, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';

export function MetricsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<FunctionMetric[]>([]);
  const [slowFunctions, setSlowFunctions] = useState<SlowFunction[]>([]);
  const [mostCalled, setMostCalled] = useState<MostCalledFunction[]>([]);
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [trend, setTrend] = useState<MetricsTrend[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    loadMetrics();
  }, [dateRange]);

  useEffect(() => {
    if (selectedFunction) {
      loadTrend();
    }
  }, [selectedFunction, dateRange]);

  const loadMetrics = async () => {
    setIsLoading(true);
    try {
      const [metricsRes, slowRes, calledRes, costsRes] = await Promise.all([
        apiClient.listMetrics({
          from: dateRange.from,
          to: dateRange.to,
          limit: 100,
        }),
        apiClient.getSlowestFunctions({
          from: dateRange.from,
          to: dateRange.to,
          limit: 10,
        }),
        apiClient.getMostCalledFunctions({
          from: dateRange.from,
          to: dateRange.to,
          limit: 10,
        }),
        apiClient.getCostSummary({
          from: dateRange.from,
          to: dateRange.to,
        }),
      ]);

      setMetrics(metricsRes.metrics);
      setSlowFunctions(slowRes.functions);
      setMostCalled(calledRes.functions);
      setCostSummary(costsRes.summary);

      // Set default selected function if not set
      if (!selectedFunction && calledRes.functions.length > 0) {
        setSelectedFunction(calledRes.functions[0].functionName);
      }
    } catch (err) {
      console.error('Failed to load metrics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTrend = async () => {
    try {
      const res = await apiClient.getMetricsTrend({
        functionName: selectedFunction,
        from: dateRange.from,
        to: dateRange.to,
      });
      setTrend(res.trend);
    } catch (err) {
      console.error('Failed to load trend:', err);
    }
  };

  // Calculate totals
  const totals = metrics.reduce(
    (acc, m) => ({
      calls: acc.calls + m.callCount,
      errors: acc.errors + m.errorCount,
      cost: acc.cost + m.totalCost,
    }),
    { calls: 0, errors: 0, cost: 0 }
  );

  const errorRate = totals.calls > 0 ? (totals.errors / totals.calls) * 100 : 0;

  // Get unique function names for dropdown
  const functionNames = [...new Set(metrics.map((m) => m.functionName))];

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Metrics</h1>
          <p className="text-gray-500 mt-1">
            Monitor function performance and costs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="w-40"
            />
            <span className="text-gray-500">to</span>
            <Input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="w-40"
            />
          </div>
          <Button variant="secondary" onClick={loadMetrics}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Calls"
          value={totals.calls.toLocaleString()}
          icon={<Activity className="w-6 h-6" />}
        />
        <StatCard
          title="Error Rate"
          value={`${errorRate.toFixed(2)}%`}
          icon={<AlertTriangle className="w-6 h-6" />}
        />
        <StatCard
          title="Total Cost"
          value={`$${totals.cost.toFixed(4)}`}
          icon={<DollarSign className="w-6 h-6" />}
        />
        <StatCard
          title="Avg Response Time"
          value={`${metrics.length > 0 ? (metrics.reduce((a, m) => a + m.avgTimeMs, 0) / metrics.length).toFixed(0) : 0}ms`}
          icon={<Clock className="w-6 h-6" />}
        />
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Trend Over Time</CardTitle>
          {functionNames.length > 0 && (
            <Select
              options={functionNames.map((f) => ({ value: f, label: f.replace(/_/g, ' ') }))}
              value={selectedFunction}
              onChange={(e) => setSelectedFunction(e.target.value)}
              className="w-48"
            />
          )}
        </CardHeader>
        <CardContent>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  tickFormatter={(v) => format(new Date(v), 'MMM d')}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  labelFormatter={(v) => format(new Date(v), 'MMM d, yyyy')}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="callCount"
                  name="Calls"
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avgTimeMs"
                  name="Avg Time (ms)"
                  stroke="#10b981"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              {isLoading ? 'Loading...' : 'No trend data available'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Called Functions */}
        <Card>
          <CardHeader>
            <CardTitle>Most Called Functions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Function</TableHeaderCell>
                  <TableHeaderCell align="right">Calls</TableHeaderCell>
                  <TableHeaderCell align="right">Error Rate</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mostCalled.length === 0 ? (
                  <EmptyState message="No data" colSpan={3} />
                ) : (
                  mostCalled.map((f) => (
                    <TableRow key={f.functionName}>
                      <TableCell className="font-medium">
                        {f.functionName.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell align="right">
                        {f.callCount.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Badge
                          variant={f.errorRate > 0.1 ? 'danger' : f.errorRate > 0.05 ? 'warning' : 'success'}
                        >
                          {(f.errorRate * 100).toFixed(1)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Slowest Functions */}
        <Card>
          <CardHeader>
            <CardTitle>Slowest Functions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Function</TableHeaderCell>
                  <TableHeaderCell align="right">Avg Time</TableHeaderCell>
                  <TableHeaderCell align="right">Calls</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slowFunctions.length === 0 ? (
                  <EmptyState message="No data" colSpan={3} />
                ) : (
                  slowFunctions.map((f) => (
                    <TableRow key={f.functionName}>
                      <TableCell className="font-medium">
                        {f.functionName.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell align="right">
                        <Badge
                          variant={f.avgTimeMs > 1000 ? 'danger' : f.avgTimeMs > 500 ? 'warning' : 'success'}
                        >
                          {f.avgTimeMs.toFixed(0)}ms
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        {f.callCount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown */}
      {costSummary && Object.keys(costSummary.byFunction).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cost Breakdown by Function</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={Object.entries(costSummary.byFunction).map(([name, data]) => ({
                  name: name.replace(/_/g, ' '),
                  cost: Number(data.cost.toFixed(4)),
                  calls: data.calls,
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip formatter={(value, name) => [name === 'cost' ? `$${value}` : value, name]} />
                <Bar dataKey="cost" name="Cost ($)" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Metrics</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Function</TableHeaderCell>
                  <TableHeaderCell>Period</TableHeaderCell>
                  <TableHeaderCell align="right">Calls</TableHeaderCell>
                  <TableHeaderCell align="right">Success</TableHeaderCell>
                  <TableHeaderCell align="right">Errors</TableHeaderCell>
                  <TableHeaderCell align="right">Avg Time</TableHeaderCell>
                  <TableHeaderCell align="right">P95 Time</TableHeaderCell>
                  <TableHeaderCell align="right">Cost</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      Loading...
                    </td>
                  </tr>
                ) : metrics.length === 0 ? (
                  <EmptyState message="No metrics data" colSpan={8} />
                ) : (
                  metrics.slice(0, 20).map((m) => (
                    <TableRow key={m.uuid}>
                      <TableCell className="font-medium">
                        {m.functionName.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell>{m.period}</TableCell>
                      <TableCell align="right">{m.callCount.toLocaleString()}</TableCell>
                      <TableCell align="right">{m.successCount.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {m.errorCount > 0 ? (
                          <span className="text-red-600">{m.errorCount}</span>
                        ) : (
                          0
                        )}
                      </TableCell>
                      <TableCell align="right">{m.avgTimeMs.toFixed(0)}ms</TableCell>
                      <TableCell align="right">{m.p95TimeMs.toFixed(0)}ms</TableCell>
                      <TableCell align="right">${m.totalCost.toFixed(4)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
